/// <reference types="../types/electron" />
import { useState, useEffect } from 'react';

/**
 * Audio Device Management Hook
 *
 * Works in BOTH web browsers and Electron desktop app:
 * - Web: Uses navigator.mediaDevices API + localStorage
 * - Electron: Uses navigator.mediaDevices API + electron-store
 *
 * Supports device selection for:
 * - Input devices (microphones)
 * - Output devices (speakers/headphones) - Chrome, Edge, Opera only
 *
 * Automatically detects device changes and refreshes the list.
 */

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

export const useAudioDevices = () => {
  const [inputDevices, setInputDevices] = useState<AudioDevice[]>([]);
  const [outputDevices, setOutputDevices] = useState<AudioDevice[]>([]);
  const [selectedInputId, setSelectedInputId] = useState<string | null>(null);
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null);
  const [isElectron, setIsElectron] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);

  useEffect(() => {
    setIsElectron(!!window.electronAPI?.isElectron);
  }, []);

  // Enumerate audio devices without requesting permissions
  // Devices will have generic labels until permission is granted
  const enumerateDevices = async (requestPermission = false) => {
    try {
      let stream: MediaStream | null = null;

      // Only request permissions if explicitly asked
      if (requestPermission && !permissionGranted) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          setPermissionGranted(true);
        } catch (error) {
          console.warn('Microphone permission not granted:', error);
          // Continue anyway - we'll get device IDs but not labels
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();

      // Stop the temporary stream immediately
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      const inputs = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.substring(0, 8)}`,
          kind: 'audioinput' as const,
        }));

      const outputs = devices
        .filter((device) => device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.substring(0, 8)}`,
          kind: 'audiooutput' as const,
        }));

      setInputDevices(inputs);
      setOutputDevices(outputs);

      // Load saved preferences from Electron or localStorage
      if (isElectron && window.electronAPI) {
        const savedInputId = await window.electronAPI.getAudioInputDevice();
        const savedOutputId = await window.electronAPI.getAudioOutputDevice();

        setSelectedInputId(savedInputId || (inputs[0]?.deviceId ?? null));
        setSelectedOutputId(savedOutputId || (outputs[0]?.deviceId ?? null));
      } else {
        const savedInputId = localStorage.getItem('audioInputDeviceId');
        const savedOutputId = localStorage.getItem('audioOutputDeviceId');

        setSelectedInputId(savedInputId || (inputs[0]?.deviceId ?? null));
        setSelectedOutputId(savedOutputId || (outputs[0]?.deviceId ?? null));
      }
    } catch (error) {
      console.error('Error enumerating audio devices:', error);
    }
  };

  // Set input device
  const setInputDevice = async (deviceId: string | null) => {
    setSelectedInputId(deviceId);

    if (isElectron && window.electronAPI) {
      await window.electronAPI.setAudioInputDevice(deviceId);
    } else {
      if (deviceId) {
        localStorage.setItem('audioInputDeviceId', deviceId);
      } else {
        localStorage.removeItem('audioInputDeviceId');
      }
    }
  };

  // Set output device
  const setOutputDevice = async (deviceId: string | null) => {
    setSelectedOutputId(deviceId);

    if (isElectron && window.electronAPI) {
      await window.electronAPI.setAudioOutputDevice(deviceId);
    } else {
      if (deviceId) {
        localStorage.setItem('audioOutputDeviceId', deviceId);
      } else {
        localStorage.removeItem('audioOutputDeviceId');
      }
    }
  };

  useEffect(() => {
    enumerateDevices();

    // Listen for device changes - always call without requesting permission
    const handleDeviceChange = () => enumerateDevices(false);
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [isElectron]);

  return {
    inputDevices,
    outputDevices,
    selectedInputId,
    selectedOutputId,
    setInputDevice,
    setOutputDevice,
    refreshDevices: () => enumerateDevices(false),
    requestPermission: () => enumerateDevices(true),
    permissionGranted,
  };
};
